import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const ISLAND_LABELS = {
  aruba: "Aruba",
  curacao: "Curaçao",
  bonaire: "Bonaire",
  stmaarten: "St. Maarten",
};

export const DigestTab = ({ subscribers }) => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [broadcastConfirm, setBroadcastConfirm] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [s, h, sc] = await Promise.all([
        api.get("/admin/digest/status"),
        api.get("/admin/digest/log"),
        api.get("/admin/digest/schedule"),
      ]);
      setStatus(s.data);
      setHistory(h.data);
      setSchedule(sc.data);
    } catch {
      toast.error("Failed to load digest status");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const sendTest = async () => {
    const phone = testPhone.trim();
    if (!phone) return toast.error("Enter a phone number first");
    setBusy(true);
    try {
      const r = await api.post("/admin/digest/send", { phone, dry_run: false });
      if (r.data.sent > 0) {
        toast.success(`Sent to ${phone} — check WhatsApp!`);
      } else {
        toast.error(`Send failed (subscribers found: ${r.data.subscribers_considered})`);
      }
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const sendDryRun = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/digest/send", { dry_run: true });
      toast.success(`Dry run: would send ${r.data.sent} message(s) — check backend logs for preview`);
      await loadAll();
    } catch {
      toast.error("Dry run failed");
    } finally {
      setBusy(false);
    }
  };

  const sendBroadcast = async () => {
    if (broadcastConfirm !== "SEND") {
      return toast.error('Type SEND to confirm broadcast');
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/digest/send", { dry_run: false });
      toast.success(`Broadcast complete — sent ${r.data.sent}, failed ${r.data.failed}, deactivated ${r.data.deactivated}`);
      setBroadcastConfirm("");
      await loadAll();
    } catch {
      toast.error("Broadcast failed");
    } finally {
      setBusy(false);
    }
  };

  const activeCount = (subscribers || []).filter((s) => s.active !== false).length;

  // ------------- Daily top-stories (Phase 2) -------------
  // Uses the same 08:00-AST cron infrastructure as the digest — this
  // trigger is what populates the /story, /verhaal, /historia pages and
  // the briefing embeds. Fires the backend job in the background; poll
  // status endpoint every 20s for the ~5 minutes it takes to complete.
  const [topStatus, setTopStatus] = useState(null);
  const [topBusy, setTopBusy] = useState(false);

  const loadTopStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/top-stories/status");
      setTopStatus(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadTopStatus(); }, [loadTopStatus]);

  const runTopStories = async () => {
    setTopBusy(true);
    try {
      await api.post("/admin/top-stories/run");
      toast.success(
        "Top-stories generation started in the background (~5 min). " +
        "The status card will update as each island completes."
      );
      // Poll for updates
      const poll = setInterval(async () => {
        await loadTopStatus();
      }, 20000);
      // Stop polling after 8 minutes regardless of state
      setTimeout(() => { clearInterval(poll); setTopBusy(false); }, 8 * 60 * 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start top-stories job");
      setTopBusy(false);
    }
  };

  const [sandbox, setSandbox] = useState(null);
  const [sandboxBusy, setSandboxBusy] = useState(false);
  const [normalizeBusy, setNormalizeBusy] = useState(false);

  const loadSandbox = useCallback(async () => {
    try {
      const r = await api.get("/admin/digest/sandbox-status");
      setSandbox(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadSandbox(); }, [loadSandbox]);

  const refreshSandbox = async () => {
    setSandboxBusy(true);
    try {
      const r = await api.post("/admin/digest/refresh-sandbox-status");
      toast.success(
        `Scanned ${r.data.inbound_msgs_scanned} inbound msgs → ` +
        `${r.data.subscribers_updated} subscribers updated`
      );
      await loadSandbox();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sandbox scan failed");
    } finally {
      setSandboxBusy(false);
    }
  };

  const normalizePhones = async () => {
    if (!window.confirm(
      "Normalize all subscriber phone numbers to E.164 format. " +
      "Invalid/ambiguous numbers will be marked INACTIVE with a reason " +
      "logged. Continue?"
    )) return;
    setNormalizeBusy(true);
    try {
      const r = await api.post("/admin/normalize-phones");
      toast.success(
        `Checked ${r.data.checked}: ${r.data.already_ok} OK, ` +
        `${r.data.normalized} normalized, ${r.data.deactivated} deactivated`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Normalize failed");
    } finally {
      setNormalizeBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-digest-tab">
      {sandbox?.is_sandbox && (
        <div
          className="rounded-lg border-l-4 border-l-[#E63946] bg-[#FEE2E2]/50 p-4"
          data-testid="digest-sandbox-warning"
        >
          <div className="flex items-start gap-3">
            <div className="text-[#E63946] text-xl leading-none pt-0.5">⚠</div>
            <div className="flex-1 space-y-2">
              <div className="font-semibold text-[#7F1D1D]">
                You're using the Twilio Sandbox — messages silently drop after 72h
              </div>
              <p className="text-sm text-[#7F1D1D]/90 leading-relaxed">
                Sender: <span className="font-mono">{sandbox.from_number}</span>.
                Every subscriber must send{" "}
                <span className="font-mono bg-white/60 px-1.5 py-0.5 rounded">
                  {sandbox.join_code || "join <code>"}
                </span>{" "}
                to <span className="font-mono">+1 415 523 8886</span> from WhatsApp
                within the last 72 hours — otherwise Twilio accepts the API call,
                returns SUCCESS, but never delivers. This is a hard sandbox
                restriction; migrate to Twilio WhatsApp Business API before any
                public launch.
              </p>
              {sandbox.subscriber_total > 0 && (
                <div className="grid grid-cols-4 gap-2 pt-2 text-xs">
                  <div className="bg-white/60 rounded p-2" data-testid="sandbox-bucket-fresh">
                    <div className="text-[#065F46] font-bold text-base">
                      {sandbox.buckets.fresh}
                    </div>
                    <div className="text-[#065F46]">Fresh &lt;72h</div>
                  </div>
                  <div className="bg-white/60 rounded p-2" data-testid="sandbox-bucket-warn">
                    <div className="text-[#92400E] font-bold text-base">
                      {sandbox.buckets.warn}
                    </div>
                    <div className="text-[#92400E]">72h–7d</div>
                  </div>
                  <div className="bg-white/60 rounded p-2" data-testid="sandbox-bucket-stale">
                    <div className="text-[#7F1D1D] font-bold text-base">
                      {sandbox.buckets.stale}
                    </div>
                    <div className="text-[#7F1D1D]">Stale &gt;7d</div>
                  </div>
                  <div className="bg-white/60 rounded p-2" data-testid="sandbox-bucket-unknown">
                    <div className="text-[#6B7280] font-bold text-base">
                      {sandbox.buckets.unknown}
                    </div>
                    <div className="text-[#6B7280]">Unknown</div>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button
                  onClick={refreshSandbox}
                  disabled={sandboxBusy}
                  className="text-xs px-3 py-1.5 rounded-md bg-[#E63946] text-white font-medium hover:bg-[#E63946]/90 disabled:opacity-50"
                  data-testid="digest-sandbox-refresh-btn"
                >
                  {sandboxBusy ? "Scanning…" : "Refresh sandbox status from Twilio"}
                </button>
                <button
                  onClick={normalizePhones}
                  disabled={normalizeBusy}
                  className="text-xs px-3 py-1.5 rounded-md border border-[#E63946] text-[#E63946] font-medium hover:bg-[#E63946]/10 disabled:opacity-50"
                  data-testid="digest-normalize-phones-btn"
                >
                  {normalizeBusy ? "Normalizing…" : "Normalize subscriber phones"}
                </button>
                {sandbox.last_scan_at && (
                  <span className="text-[11px] text-[#7F1D1D]/70">
                    Last scan: {new Date(sandbox.last_scan_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card-soft p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#1A1A1A]">Twilio WhatsApp Status</h3>
          <button
            onClick={loadAll}
            className="text-xs text-[#0AADA0] hover:underline"
            data-testid="digest-refresh-btn"
          >
            Refresh
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-[#6B7280] mb-1">Twilio</div>
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${status?.twilio_configured ? "bg-[#3BB273]" : "bg-[#E63946]"}`} />
              <span className="font-medium">{status?.twilio_configured ? "Configured" : "Not configured"}</span>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-[#6B7280] mb-1">Sender</div>
            <div className="font-mono text-xs text-[#1A1A1A]">{status?.from_number || "—"}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-[#6B7280] mb-1">Active Subscribers</div>
            <div className="font-medium text-[#1A1A1A]">{activeCount}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-[#6B7280] mb-1">Last Run</div>
            <div className="text-xs text-[#1A1A1A]">
              {status?.last_run?.ran_at
                ? new Date(status.last_run.ran_at).toLocaleString()
                : "Never"}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-[#EEEEEE] space-y-1" data-testid="digest-schedule-block">
          <div className="text-[12px] text-[#6B7C82]" data-testid="digest-next-scheduled">
            {schedule?.next_run
              ? `Next scheduled send: ${new Date(schedule.next_run).toLocaleString(
                  undefined,
                  { timeZone: "America/Aruba", dateStyle: "medium", timeStyle: "short" }
                )} AST`
              : "Next scheduled send: —"}
          </div>
          <div className="text-[12px] text-[#6B7C82]" data-testid="digest-last-automatic">
            {schedule?.last_automatic_send
              ? `Last automatic send: ${
                  schedule.last_automatic_send.send_date
                } ${
                  (schedule.last_automatic_send.send_time || "").slice(11, 16)
                } — ${schedule.last_automatic_send.total_recipients} subscribers`
              : "No automatic sends yet"}
          </div>
        </div>
      </div>

      <div className="card-soft p-5">
        <h3 className="font-semibold text-[#1A1A1A] mb-2">Send Test Digest</h3>
        <p className="text-sm text-[#6B7280] mb-4">
          Send to one phone number. Recipient must have joined the Twilio sandbox first
          (WhatsApp <span className="font-mono">join &lt;your-code&gt;</span> to <span className="font-mono">+1 415 523 8886</span>).
        </p>
        <div className="flex flex-col md:flex-row gap-2">
          <input
            type="tel"
            placeholder="+2975932727"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            className="flex-1 px-4 py-2.5 rounded-lg border border-[#E5E5E5] focus:border-[#0AADA0] focus:outline-none text-sm"
            data-testid="digest-test-phone-input"
          />
          <button
            onClick={sendTest}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg bg-[#0AADA0] text-white text-sm font-medium hover:bg-[#0AADA0]/90 disabled:opacity-50"
            data-testid="digest-send-test-btn"
          >
            {busy ? "Sending..." : "Send Test"}
          </button>
          <button
            onClick={sendDryRun}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg border border-[#E5E5E5] text-[#1A1A1A] text-sm font-medium hover:bg-[#FAFAFA] disabled:opacity-50"
            data-testid="digest-dry-run-btn"
          >
            Dry Run (log only)
          </button>
        </div>
      </div>

      <div className="card-soft p-5 border-l-4 border-l-[#E63946]">
        <h3 className="font-semibold text-[#1A1A1A] mb-2">Send to All Subscribers Now</h3>
        <p className="text-sm text-[#6B7280] mb-4">
          The scheduled cron runs daily at 8 AM AST. Use this only to send manually outside that window.
          Will send to <strong>{activeCount}</strong> active subscriber{activeCount === 1 ? "" : "s"}.
        </p>
        <div className="flex flex-col md:flex-row gap-2">
          <input
            type="text"
            placeholder='Type "SEND" to confirm'
            value={broadcastConfirm}
            onChange={(e) => setBroadcastConfirm(e.target.value)}
            className="flex-1 px-4 py-2.5 rounded-lg border border-[#E5E5E5] focus:border-[#E63946] focus:outline-none text-sm"
            data-testid="digest-broadcast-confirm-input"
          />
          <button
            onClick={sendBroadcast}
            disabled={busy || broadcastConfirm !== "SEND" || activeCount === 0}
            className="px-4 py-2.5 rounded-lg bg-[#E63946] text-white text-sm font-medium hover:bg-[#E63946]/90 disabled:opacity-40"
            data-testid="digest-broadcast-btn"
          >
            {busy ? "Broadcasting..." : "Broadcast Now"}
          </button>
        </div>
      </div>

      <div className="card-soft overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <h3 className="font-semibold text-[#1A1A1A]">Recent Runs</h3>
          <span className="text-xs text-[#6B7280]">Last {history.length}</span>
        </div>
        <div className="px-5 py-2 text-[11px] text-[#6B7280] bg-[#FAFAFA] border-b border-[#E5E5E5]">
          <span className="font-medium">Sent</span> = Twilio accepted API call ·
          <span className="font-medium text-[#065F46]"> Delivered</span> = message reached the phone ·
          <span className="font-medium text-[#7F1D1D]"> Failed</span> = Twilio delivery error (async)
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] bg-[#FAFAFA]">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Mode</th>
              <th className="px-4 py-3">Sent</th>
              <th className="px-4 py-3 text-[#065F46]">Delivered</th>
              <th className="px-4 py-3 text-[#7F1D1D]">Failed</th>
              <th className="px-4 py-3">Queued</th>
              <th className="px-4 py-3">Digest ID</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-[#6B7280]">No runs yet.</td></tr>
            ) : history.map((h, idx) => {
              const gap = (h.sent ?? 0) - (h.delivered ?? 0) - (h.failed ?? 0) - (h.queued ?? 0);
              return (
                <tr key={idx} className="border-t border-[#E5E5E5]" data-testid={`digest-run-row-${idx}`}>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">
                    {h.ran_at ? new Date(h.ran_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {h.dry_run ? (
                      <span className="px-2 py-0.5 rounded-full bg-[#FAFAFA] text-[#6B7280]">dry-run</span>
                    ) : h.trigger === "cron" ? (
                      <span className="px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E40AF]">cron</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-[#0AADA0]/10 text-[#0AADA0]">manual</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#1A1A1A]">{h.sent ?? 0}</td>
                  <td className="px-4 py-3 font-medium text-[#065F46]">{h.delivered ?? 0}</td>
                  <td className="px-4 py-3 font-medium text-[#7F1D1D]">{h.failed ?? 0}</td>
                  <td className="px-4 py-3 text-[#6B7280]">
                    {h.queued ?? 0}
                    {gap > 0 && (
                      <span className="ml-1 text-[10px] text-[#6B7280]" title="Not yet reported by Twilio">
                        (+{gap} pre-webhook)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[10px] font-mono text-[#6B7280] truncate max-w-[120px]">
                    {h.digest_id || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Daily Top Stories (Phase 2 canonical top-5 per island) */}
      <div className="card-soft p-5" data-testid="admin-top-stories-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#1A1A1A]">
            Daily Top Stories
          </h3>
          <button
            className="btn-primary text-[13px] px-3 py-1.5 disabled:opacity-50"
            onClick={runTopStories}
            disabled={topBusy}
            data-testid="admin-run-top-stories-btn"
          >
            {topBusy ? "Running…" : "Run now"}
          </button>
        </div>
        <p className="text-[12px] text-[#6B7C82] mb-4">
          Regenerates the canonical top-5 for every island and the native
          NL/ES summaries used by the story pages and briefing embeds.
          Runs automatically at 08:01 AST — use this only to force an
          immediate refresh. Job runs in the background and takes ~5 min.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="admin-top-stories-status">
          {["aruba", "curacao", "bonaire", "stmaarten"].map((isl) => {
            const s = topStatus?.islands?.[isl];
            const ok = s?.is_today && s?.story_count === 5;
            return (
              <div
                key={isl}
                className="p-2.5 border border-[#EEEEEE] rounded-md text-[12px]"
                data-testid={`admin-top-stories-${isl}`}
              >
                <div className="font-medium text-[#1A1A1A]">{ISLAND_LABELS[isl]}</div>
                <div className={ok ? "text-[#0AADA0]" : "text-[#B45309]"}>
                  {s ? `${s.story_count} / 5` : "—"}
                </div>
                <div className="text-[10px] text-[#6B7C82] mt-0.5">
                  {s?.send_date ? (s.is_today ? "today" : `${s.send_date} (stale)`) : "no data"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
